import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EndRecommendCard } from '~/components/end-recommend-card';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ClickPosition } from '~/utils/click-position';
import { ModuleType } from '~/utils/module-type';
import { Button, ButtonSize } from '@lemon8/design';
import ArrowClockwise from 'icon/arrow_clockwise.svg';
import { useActiveToFeedForyou } from '~/utils/active/use-active-to-feed';
import useReflowClick from '~/utils/use-reflow-click';
import { IArticleRelatedPost } from 'shared/atoms';
import { IArticleDetail } from 'shared/models';
import { Loading } from '~/components/loading';

import './style.scss';

interface IVideoEndRecommendProps {
  show: boolean;
  article: IArticleDetail;
  relatedPost: IArticleRelatedPost;
  onClickReplay: () => void;
}

const VideoEndRecommendNoContent: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="video-end-recommend-empty">
      <span>{t('no_content')}</span>
    </div>
  );
};

const VideoEndRecommendLoading: FC = () => {
  return (
    <div className="video-end-recommend-loading">
      <Loading variant="ball" />
    </div>
  );
};

export const VideoEndRecommend: FC<IVideoEndRecommendProps> = (props) => {
  const { t } = useTranslation();
  const activeToFeedForyou = useActiveToFeedForyou();

  const { show, relatedPost, onClickReplay, article } = props;

  const observedRef = useViewTracking(
    {
      onView: useCallback(() => {
        window.Tea('wap_module_show', {
          current_page_module: ModuleType.main_related_post,
          group_id: article.groupId,
        });
      }, []),
    },
    [show],
  );

  const mainSeeMoreRef = useViewTracking(
    {
      onView: useCallback(() => {
        window.Tea('wap_cta_show', {
          current_position: ClickPosition.main_see_more_btn,
          current_page_module: ModuleType.main_related_post,
          group_id: article.groupId,
        });
      }, []),
    },
    [show],
  );

  const handleEndRecommendBtnClick = useReflowClick(
    {
      clickEvent: () => ({
        current_position: ClickPosition.main_see_more_btn,
      }),
      reflowBehavior: () => {
        activeToFeedForyou(ClickPosition.main_see_more_btn);
      },
      defaultBehavior: () => {
        activeToFeedForyou(ClickPosition.main_see_more_btn);
      },
    },
    [activeToFeedForyou],
  );

  const replayBtnRef = useViewTracking(
    {
      onView: useCallback(() => {
        window.Tea('wap_cta_show', {
          current_position: ClickPosition.main_video_replay,
          current_page_module: ModuleType.main_related_post,
        });
      }, []),
    },
    [show],
  );

  const handleReplayBtnClick = useCallback(() => {
    window.Tea('wap_click', {
      current_position: ClickPosition.main_video_replay,
    });
    onClickReplay();
  }, [onClickReplay]);

  const itemsToShow = useMemo(() => {
    const { items } = relatedPost;

    return items.filter((item) => item.groupId !== article.groupId).slice(0, 5);
  }, [relatedPost, article]);

  if (!show) {
    return null;
  }

  return (
    <div className="video-end-recommend" ref={observedRef}>
      <p className="video-end-recommend-title">{t('seo_v3_postDetail_recommended_title')}</p>
      <div className="video-end-recommend-main">
        {!relatedPost.items.length && relatedPost.hasMore && <VideoEndRecommendLoading />}
        {!relatedPost.items.length && !relatedPost.hasMore && <VideoEndRecommendNoContent />}
        {Boolean(relatedPost.items.length) && (
          <Swiper resistanceRatio={0} slidesPerView={'auto'} nested>
            {itemsToShow.map((item, index) => (
              <SwiperSlide key={item.groupId}>
                <EndRecommendCard article={item} size="normal" rank={index} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
      <div className="active-btn" ref={mainSeeMoreRef}>
        <Button
          variant="Secondary"
          size={ButtonSize.large}
          onClick={handleEndRecommendBtnClick}
          title={t('seo_v3_postDetail_openApp_btn')}
        />
      </div>
      <div className="replay-btn" ref={replayBtnRef} onClick={handleReplayBtnClick}>
        <ArrowClockwise />
        <span>{t('seo_v3_postDetail_replay_btn')}</span>
      </div>
    </div>
  );
};
